import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  EventAvailable as EventAvailableIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { clientServiceV2 } from 'src/clientService';
import {
  ProjectSessionResponse,
  ProjectSessionCreate,
  ProjectSessionUpdate,
  SessionStatus,
} from 'src/api2/Api';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

interface ProjectSessionsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName?: string;
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    color: '#FFF',
    borderRadius: '10px',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(16, 185, 129, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#10B981' },
    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
};

const getStatusChip = (status: SessionStatus | string) => {
  const normStatus = String(status).toLowerCase();
  switch (normStatus) {
    case 'active':
      return (
        <Chip
          label="Active"
          size="small"
          sx={{
            bgcolor: 'rgba(16, 185, 129, 0.12)',
            color: '#10B981',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            fontWeight: 700,
            fontSize: '11px',
            textTransform: 'uppercase',
          }}
        />
      );
    case 'upcoming':
      return (
        <Chip
          label="Upcoming"
          size="small"
          sx={{
            bgcolor: 'rgba(59, 130, 246, 0.12)',
            color: '#3B82F6',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            fontWeight: 700,
            fontSize: '11px',
            textTransform: 'uppercase',
          }}
        />
      );
    case 'completed':
      return (
        <Chip
          label="Completed"
          size="small"
          sx={{
            bgcolor: 'rgba(156, 163, 175, 0.12)',
            color: '#9CA3AF',
            border: '1px solid rgba(156, 163, 175, 0.3)',
            fontWeight: 700,
            fontSize: '11px',
            textTransform: 'uppercase',
          }}
        />
      );
    default:
      return (
        <Chip
          label={status}
          size="small"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            color: '#DDD',
            fontWeight: 600,
            fontSize: '11px',
          }}
        />
      );
  }
};

export const ProjectSessionsDialog: FC<ProjectSessionsDialogProps> = ({
  open,
  onClose,
  projectId,
  projectName = 'Project',
}) => {
  const { showSnackbar } = useSnackbar();

  // Sessions state
  const [sessions, setSessions] = useState<ProjectSessionResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  // Form modal state (Create / Edit)
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingSession, setEditingSession] = useState<ProjectSessionResponse | null>(null);
  const [sessionName, setSessionName] = useState<string>('');
  const [sessionStartDate, setSessionStartDate] = useState<string>('');
  const [sessionEndDate, setSessionEndDate] = useState<string>('');
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>(SessionStatus.Upcoming);
  const [formError, setFormError] = useState<string>('');

  // Delete modal state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [sessionToDelete, setSessionToDelete] = useState<ProjectSessionResponse | null>(null);

  // Load sessions list
  const loadSessions = useCallback(async () => {
    if (!open || !projectId) return;
    setLoading(true);
    try {
      const response = await clientServiceV2.api.listProjectSessionsApiV2ProjectsProjectIdSessionsGet(
        projectId,
        { secure: true }
      );
      const data = response.data?.data || [];
      setSessions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to fetch project sessions:', err);
      showSnackbar({
        message: err?.response?.data?.detail || 'Failed to load project sessions',
        type: SnackbarType.ERROR,
      });
    } finally {
      setLoading(false);
    }
  }, [open, projectId, showSnackbar]);

  useEffect(() => {
    if (open) {
      loadSessions();
    }
  }, [open, loadSessions]);

  // Open Form for Creation
  const handleOpenCreate = () => {
    setEditingSession(null);
    setSessionName('');
    setSessionStartDate('');
    setSessionEndDate('');
    setSessionStatus(SessionStatus.Upcoming);
    setFormError('');
    setIsFormOpen(true);
  };

  // Open Form for Editing
  const handleOpenEdit = (session: ProjectSessionResponse) => {
    setEditingSession(session);
    setSessionName(session.session_name);
    setSessionStartDate(session.session_start_date || '');
    setSessionEndDate(session.session_end_date || '');
    setSessionStatus((session.status as SessionStatus) || SessionStatus.Upcoming);
    setFormError('');
    setIsFormOpen(true);
  };

  // Close Form
  const handleCloseForm = () => {
    if (actionLoading) return;
    setIsFormOpen(false);
    setEditingSession(null);
    setFormError('');
  };

  // Save Session (Create or Update)
  const handleSaveSession = async () => {
    const trimmedName = sessionName.trim();
    if (!trimmedName) {
      setFormError('Session name is required.');
      return;
    }
    if (!sessionStartDate) {
      setFormError('Start date is required.');
      return;
    }
    if (sessionEndDate && new Date(sessionEndDate) < new Date(sessionStartDate)) {
      setFormError('End date must be on or after start date.');
      return;
    }

    setActionLoading(true);
    setFormError('');
    try {
      if (editingSession) {
        // Update existing session
        const updatePayload: ProjectSessionUpdate = {
          session_name: trimmedName,
          session_start_date: sessionStartDate,
          session_end_date: sessionEndDate || null,
          status: sessionStatus,
        };
        await clientServiceV2.api.updateProjectSessionApiV2ProjectsProjectIdSessionsSessionIdPut(
          projectId,
          editingSession.session_id,
          updatePayload,
          { secure: true }
        );
        showSnackbar({ message: 'Session updated successfully', type: SnackbarType.SUCCESS });
      } else {
        // Create new session
        const createPayload: ProjectSessionCreate = {
          session_name: trimmedName,
          session_start_date: sessionStartDate,
          session_end_date: sessionEndDate || null,
          status: sessionStatus,
        };
        await clientServiceV2.api.createProjectSessionApiV2ProjectsProjectIdSessionsPost(
          projectId,
          createPayload,
          { secure: true }
        );
        showSnackbar({ message: 'Session created successfully', type: SnackbarType.SUCCESS });
      }
      setIsFormOpen(false);
      setEditingSession(null);
      await loadSessions();
    } catch (err: any) {
      console.error('Failed to save project session:', err);
      const detail = err?.response?.data?.detail || err?.data?.detail || 'Failed to save project session';
      setFormError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      showSnackbar({
        message: typeof detail === 'string' ? detail : 'Failed to save project session',
        type: SnackbarType.ERROR,
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Open Delete Confirmation
  const handleOpenDelete = (session: ProjectSessionResponse) => {
    setSessionToDelete(session);
    setIsDeleteDialogOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    setActionLoading(true);
    try {
      await clientServiceV2.api.deleteProjectSessionApiV2ProjectsProjectIdSessionsSessionIdDelete(
        projectId,
        sessionToDelete.session_id,
        { secure: true }
      );
      showSnackbar({ message: 'Session deleted successfully', type: SnackbarType.SUCCESS });
      setIsDeleteDialogOpen(false);
      setSessionToDelete(null);
      await loadSessions();
    } catch (err: any) {
      console.error('Failed to delete project session:', err);
      const detail = err?.response?.data?.detail || err?.data?.detail || 'Failed to delete project session';
      showSnackbar({
        message: typeof detail === 'string' ? detail : 'Failed to delete project session',
        type: SnackbarType.ERROR,
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      {/* Main Sessions Management Dialog */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0E1015',
            border: '1px solid #222',
            borderRadius: '16px',
            color: '#FFF',
            p: 1,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '12px',
                bgcolor: 'rgba(16, 185, 129, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#10B981',
              }}
            >
              <EventAvailableIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFF', lineHeight: 1.2 }}>
                Project Sessions
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                {projectName} • Manage monitoring cycles and deployment sessions
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                bgcolor: '#10B981',
                color: '#FFF',
                fontWeight: 700,
                borderRadius: '10px',
                textTransform: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#059669' },
              }}
            >
              New Session
            </Button>
            <IconButton onClick={onClose} sx={{ color: '#888', '&:hover': { color: '#FFF' } }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: '#10B981' }} />
              <Typography variant="body2" sx={{ color: '#888' }}>
                Loading sessions...
              </Typography>
            </Box>
          ) : sessions.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 3,
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '12px',
                border: '1px dashed #282828',
              }}
            >
              <CalendarIcon sx={{ fontSize: 48, color: '#444', mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#FFF', mb: 0.5 }}>
                No Sessions Created Yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#777', maxWidth: 420, mx: 'auto', mb: 3 }}>
                Organize device deployments and field collections into time-bound project sessions.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
                sx={{
                  borderColor: '#10B981',
                  color: '#10B981',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)', borderColor: '#10B981' },
                }}
              >
                Create First Session
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  border: '1px solid #1E2229',
                  borderRadius: '12px',
                }}
              >
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)' }}>
                      <TableCell sx={{ color: '#999', fontWeight: 600, borderBottom: '1px solid #222' }}>
                        Session Name
                      </TableCell>
                      <TableCell sx={{ color: '#999', fontWeight: 600, borderBottom: '1px solid #222' }}>
                        Start Date
                      </TableCell>
                      <TableCell sx={{ color: '#999', fontWeight: 600, borderBottom: '1px solid #222' }}>
                        End Date
                      </TableCell>
                      <TableCell sx={{ color: '#999', fontWeight: 600, borderBottom: '1px solid #222' }}>
                        Status
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: '#999', fontWeight: 600, borderBottom: '1px solid #222' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow
                        key={session.session_id}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.02)' },
                          borderBottom: '1px solid #1A1E24',
                        }}
                      >
                        <TableCell sx={{ color: '#FFF', fontWeight: 600, borderBottom: '1px solid #1E2229' }}>
                          {session.session_name}
                        </TableCell>
                        <TableCell sx={{ color: '#BBB', borderBottom: '1px solid #1E2229' }}>
                          {formatDate(session.session_start_date, 'd MMM yyyy')}
                        </TableCell>
                        <TableCell sx={{ color: '#BBB', borderBottom: '1px solid #1E2229' }}>
                          {session.session_end_date ? formatDate(session.session_end_date, 'd MMM yyyy') : '—'}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #1E2229' }}>
                          {getStatusChip(session.status)}
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid #1E2229' }}>
                          <Tooltip title="Edit Session">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(session)}
                              sx={{ color: '#888', '&:hover': { color: '#10B981' } }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Session">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(session)}
                              sx={{ color: '#888', '&:hover': { color: '#EF4444' } }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1, borderTop: '1px solid #1E2229' }}>
          <Button
            onClick={onClose}
            sx={{
              color: '#BBB',
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              '&:hover': { color: '#FFF', bgcolor: 'rgba(255, 255, 255, 0.05)' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Session Sub-Dialog */}
      <Dialog
        open={isFormOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#12141A',
            border: '1px solid #282C35',
            borderRadius: '16px',
            color: '#FFF',
          },
        }}
      >
        <Box sx={{ p: 3, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFF' }}>
            {editingSession ? 'Edit Session' : 'Create New Session'}
          </Typography>
          <IconButton onClick={handleCloseForm} sx={{ color: '#888', '&:hover': { color: '#FFF' } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {formError && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#F87171',
                fontSize: '13px',
              }}
            >
              {formError}
            </Box>
          )}

          <TextField
            label="Session Name"
            placeholder="e.g. Q1 Monitoring Cycle"
            value={sessionName}
            onChange={(e) => {
              setSessionName(e.target.value);
              if (formError) setFormError('');
            }}
            fullWidth
            required
            sx={inputSx}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={sessionStartDate}
              onChange={(e) => {
                setSessionStartDate(e.target.value);
                if (formError) setFormError('');
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              sx={inputSx}
            />

            <TextField
              label="End Date (Optional)"
              type="date"
              value={sessionEndDate}
              onChange={(e) => {
                setSessionEndDate(e.target.value);
                if (formError) setFormError('');
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={inputSx}
            />
          </Box>

          <TextField
            select
            label="Status"
            value={sessionStatus}
            onChange={(e) => setSessionStatus(e.target.value as SessionStatus)}
            fullWidth
            sx={inputSx}
          >
            <MenuItem value={SessionStatus.Upcoming}>Upcoming</MenuItem>
            <MenuItem value={SessionStatus.Active}>Active</MenuItem>
            <MenuItem value={SessionStatus.Completed}>Completed</MenuItem>
          </TextField>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #20242C' }}>
          <Button
            onClick={handleCloseForm}
            disabled={actionLoading}
            sx={{
              color: '#BBB',
              textTransform: 'none',
              borderRadius: '8px',
              px: 2.5,
              '&:hover': { color: '#FFF' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSession}
            disabled={actionLoading}
            sx={{
              bgcolor: '#10B981',
              color: '#FFF',
              fontWeight: 700,
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {actionLoading ? (
              <CircularProgress size={20} sx={{ color: '#FFF' }} />
            ) : editingSession ? (
              'Save Changes'
            ) : (
              'Create Session'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => !actionLoading && setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#12141A',
            border: '1px solid #282C35',
            borderRadius: '16px',
            color: '#FFF',
            p: 1,
          },
        }}
      >
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFF', mb: 1 }}>
            Delete Session
          </Typography>
          <Typography variant="body2" sx={{ color: '#AAA' }}>
            Are you sure you want to delete{' '}
            <strong style={{ color: '#FFF' }}>{sessionToDelete?.session_name}</strong>? This action cannot be
            undone.
          </Typography>
        </Box>
        <DialogActions sx={{ p: 2.5, pt: 2 }}>
          <Button
            onClick={() => setIsDeleteDialogOpen(false)}
            disabled={actionLoading}
            sx={{
              color: '#BBB',
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': { color: '#FFF' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={actionLoading}
            sx={{
              bgcolor: '#EF4444',
              color: '#FFF',
              fontWeight: 700,
              borderRadius: '8px',
              textTransform: 'none',
              px: 2.5,
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ color: '#FFF' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectSessionsDialog;
